import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CheckIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import {
  contratosService,
  getOpenAmountsTotal,
  isContractAmountCurrent,
} from '../../services/contratos'
import { estoqueService } from '../../services/estoque'
import { marketingParametersService } from '../../services/marketingParameters'
import type { Contract, ContractInput, ContractRenewalType, ContractStatus } from '../../types/contratos'
import type { EstoqueFornecedor } from '../../types/estoque'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  PERM_LOADING,
  paginateRows,
  sortRows,
  statusBadgeClass as activeBadgeClass,
  PAGE_SIZE,
} from '../Estoque/estoqueUi'
import EstoquePagination from '../Estoque/EstoquePagination'
import ContratosConfirmModal from './ContratosConfirmModal'
import ContratosFormModal from './ContratosFormModal'
import {
  ConfirmCancelledError,
  SELECT_CLASS,
  formatCurrency,
  formatDateOnlyPtBr,
  statusBadgeClass as draftStatusBadgeClass,
} from './notasUi'
import { formatRequestNumber, parseRequestNumberFilter } from './solicitacaoUi'

type ContractListFilters = {
  search: string
  status: ContractStatus | ''
  isActive: '' | 'active' | 'inactive'
  supplierId: string
  departmentId: string
  dateFrom: string
  dateTo: string
  requestNumber: string
}

const DEFAULT_FILTERS: ContractListFilters = {
  search: '',
  status: '',
  isActive: '',
  supplierId: '',
  departmentId: '',
  dateFrom: '',
  dateTo: '',
  requestNumber: '',
}

function contractStatusLabel(status: string | null | undefined): string {
  return status === 'confirmed' ? 'Confirmado' : 'Rascunho'
}

const MODULE_NAME = 'marketing_contratos'

const RENEWAL_LABELS: Record<ContractRenewalType, string> = {
  recorrente: 'Recorrente',
  aditivo: 'Com aditivo',
}

const MarketingContratos: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete, logView } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [contracts, setContracts] = useState<Contract[]>([])
  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [departments, setDepartments] = useState<
    { id: string; name: string; display_name: string }[]
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<ContractListFilters>(() => ({
    ...DEFAULT_FILTERS,
    search: searchParams.get('q')?.trim() || '',
  }))

  const [sortKey, setSortKey] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Contract | null>(null)
  const editingRef = useRef<Contract | null>(null)
  const [viewing, setViewing] = useState<Contract | null>(null)
  const [saving, setSaving] = useState(false)
  const [confirming, setConfirming] = useState<Contract | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)
  const confirmResolverRef = useRef<((ok: boolean) => void) | null>(null)

  const setEditingContract = useCallback((contract: Contract | null) => {
    editingRef.current = contract
    setEditing(contract)
  }, [])

  const closeConfirmModal = useCallback((ok: boolean) => {
    const resolve = confirmResolverRef.current
    confirmResolverRef.current = null
    // Cancelar fecha já; confirmar mantém o modal aberto com busy até o fim.
    if (!ok) setConfirming(null)
    resolve?.(ok)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [list, fornecedoresData, departmentsData] = await Promise.all([
        contratosService.list(true),
        estoqueService.listFornecedores(true),
        marketingParametersService.listDepartmentsActive(),
      ])
      setContracts(list)
      setFornecedores(fornecedoresData)
      setDepartments(departmentsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar contratos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    const term = filters.search.trim().toLowerCase()
    const requestNumber = parseRequestNumberFilter(filters.requestNumber)
    return contracts.filter((row) => {
      if (filters.status && (row.status || 'draft') !== filters.status) return false
      if (filters.isActive === 'active' && !row.is_active) return false
      if (filters.isActive === 'inactive' && row.is_active) return false
      if (filters.supplierId && row.supplier_id !== filters.supplierId) return false
      if (filters.departmentId && row.department_id !== filters.departmentId) return false
      const adhesion = row.adhesion_date?.slice(0, 10) || ''
      if (filters.dateFrom) {
        if (!adhesion || adhesion < filters.dateFrom) return false
      }
      if (filters.dateTo) {
        if (!adhesion || adhesion > filters.dateTo) return false
      }
      if (requestNumber != null && row.request?.request_number !== requestNumber) {
        return false
      }
      if (term) {
        const haystack = `${row.name} ${row.contract_number || ''}`.toLowerCase()
        if (!haystack.includes(term)) return false
      }
      return true
    })
  }, [contracts, filters])

  const sorted = useMemo(
    () =>
      sortRows(filtered, sortKey, sortOrder, (row, key) => {
        if (key === 'name') return row.name
        if (key === 'number') return row.contract_number || ''
        if (key === 'supplier') return row.supplier?.name || ''
        if (key === 'department') return row.department?.display_name || row.department?.name || ''
        if (key === 'adhesion') return row.adhesion_date || ''
        if (key === 'renewal') return row.renewal_type === 'recorrente' ? row.adhesion_date || '' : ''
        if (key === 'termination') return row.termination_date || ''
        if (key === 'status') return row.status || 'draft'
        if (key === 'active') return row.is_active ? 1 : 0
        if (key === 'amount') return getOpenAmountsTotal(row.amounts)
        return ''
      }),
    [filtered, sortKey, sortOrder]
  )
  const pageRows = paginateRows(sorted, page)

  const updateFilter = <K extends keyof ContractListFilters>(
    key: K,
    value: ContractListFilters[K]
  ) => {
    setFilters((f) => ({ ...f, [key]: value }))
    setPage(1)
  }

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const openCreate = () => {
    setEditingContract(null)
    setModalOpen(true)
  }

  const openEdit = (row: Contract) => {
    if (row.status !== 'draft') {
      window.alert('Somente rascunhos podem ser editados.')
      return
    }
    setEditingContract(row)
    setModalOpen(true)
  }

  const openView = (row: Contract) => {
    setViewing(row)
    void logView('contract', row.id, `Visualizou contrato: ${row.name}`)
  }

  const handleOpenAttachment = async (filePath: string) => {
    const url = await contratosService.getAttachmentUrl(filePath)
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
    else window.alert('Não foi possível abrir o anexo.')
  }

  const handleSave = async (input: ContractInput, pendingFiles: File[]): Promise<Contract> => {
    setSaving(true)
    try {
      let saved: Contract
      const current = editingRef.current
      if (current) {
        saved = await contratosService.update(current.id, input, user?.id)
        void logUpdate(
          'contract',
          saved.id,
          `Atualizou contrato: ${saved.name}`,
          {
            name: current.name,
            contract_number: current.contract_number,
            contact_name: current.contact_name,
            contact_phone: current.contact_phone,
            contact_email: current.contact_email,
            supplier_id: current.supplier_id,
            department_id: current.department_id,
            responsible_id: current.responsible_id,
            campaign_id: current.campaign_id,
            event_id: current.event_id,
            status: current.status,
            request_id: current.request_id,
          },
          {
            name: saved.name,
            contract_number: saved.contract_number,
            contact_name: saved.contact_name,
            contact_phone: saved.contact_phone,
            contact_email: saved.contact_email,
            supplier_id: saved.supplier_id,
            department_id: saved.department_id,
            responsible_id: saved.responsible_id,
            campaign_id: saved.campaign_id,
            event_id: saved.event_id,
            status: saved.status,
            request_id: saved.request_id,
          }
        )
      } else {
        saved = await contratosService.create(input, user?.id)
        void logCreate('contract', saved.id, `Criou contrato: ${saved.name}`, {
          name: saved.name,
          contract_number: saved.contract_number,
          contact_name: saved.contact_name,
          contact_phone: saved.contact_phone,
          contact_email: saved.contact_email,
          supplier_id: saved.supplier_id,
          department_id: saved.department_id,
          responsible_id: saved.responsible_id,
          campaign_id: saved.campaign_id,
          event_id: saved.event_id,
          request_id: saved.request_id,
          status: saved.status,
        })
      }

      for (const file of pendingFiles) {
        const att = await contratosService.uploadAttachment(file, saved.id, user?.id)
        void logCreate('contract_attachment', att.id, `Anexou arquivo ao contrato ${saved.name}`, {
          file_name: att.file_name,
        })
      }

      const refreshed = (await contratosService.getById(saved.id)) || saved
      setEditingContract(refreshed)
      await load()
      return refreshed
    } finally {
      setSaving(false)
    }
  }

  const askConfirmContract = (contract: Contract) =>
    new Promise<boolean>((resolve) => {
      confirmResolverRef.current = resolve
      setConfirming(contract)
    })

  const runConfirmContract = async (contract: Contract) => {
    setConfirmBusy(true)
    setError(null)
    try {
      const confirmed = await contratosService.confirmContract(contract.id, user?.id)
      void logUpdate(
        'contract',
        confirmed.id,
        `Confirmou contrato: ${confirmed.name}`,
        { status: contract.status, confirmed_at: contract.confirmed_at },
        { status: confirmed.status, confirmed_at: confirmed.confirmed_at }
      )
      await load()
    } finally {
      setConfirmBusy(false)
    }
  }

  const handleConcludeFromModal = async (contract: Contract) => {
    if (contract.status !== 'draft') {
      throw new Error('Somente rascunhos podem ser confirmados.')
    }
    const ok = await askConfirmContract(contract)
    if (!ok) throw new ConfirmCancelledError()
    try {
      await runConfirmContract(contract)
    } finally {
      setConfirming(null)
    }
  }

  const handleDelete = async (row: Contract) => {
    if (row.status !== 'draft') {
      window.alert('Somente rascunhos podem ser excluídos.')
      return
    }
    if (!window.confirm(`Excluir o contrato "${row.name}"?`)) return
    try {
      await contratosService.softDelete(row.id, {
        deleted_by: user?.id,
        deleted_reason: 'Exclusão pela tela de contratos',
      })
      void logDelete('contract', row.id, `Excluiu contrato: ${row.name}`, { name: row.name })
      await load()
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao excluir contrato.')
    }
  }

  const openConfirm = (row: Contract) => {
    if (row.status !== 'draft') return
    void (async () => {
      const ok = await askConfirmContract(row)
      if (!ok) return
      try {
        await runConfirmContract(row)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao confirmar contrato.')
      } finally {
        setConfirming(null)
      }
    })()
  }

  const handleConfirmModalConfirm = () => {
    setConfirmBusy(true)
    closeConfirmModal(true)
  }

  const viewingOpenTotal = viewing ? getOpenAmountsTotal(viewing.amounts) : 0

  return (
    <div className="flex h-full flex-col overflow-x-hidden overflow-y-auto">
      <PermissionGuard moduleName={MODULE_NAME} permissionType="show">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-start justify-between gap-3">
            <div>
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Contratos
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                Cadastre contratos com solicitação, valores, pagamentos e anexos. Confirme os
                rascunhos na lista.
              </Text>
            </div>
            {canInsert && (
              <PermissionGuard
                moduleName={MODULE_NAME}
                permissionType="insert"
                loadingComponent={PERM_LOADING}
                fallback={null}
              >
                <Button variant="solid" onClick={openCreate} className="flex items-center gap-1">
                  <PlusIcon className="h-5 w-5" />
                  Novo contrato
                </Button>
              </PermissionGuard>
            )}
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Busca
              </label>
              <Input
                className="w-full"
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                placeholder="Nome ou número"
              />
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Solicitação
              </label>
              <Input
                className="w-full"
                value={filters.requestNumber}
                onChange={(e) => updateFilter('requestNumber', e.target.value)}
                placeholder="#123"
              />
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Situação
              </label>
              <select
                className={SELECT_CLASS + ' w-full'}
                value={filters.status}
                onChange={(e) =>
                  updateFilter('status', e.target.value as ContractStatus | '')
                }
              >
                <option value="">Todas</option>
                <option value="draft">Rascunho</option>
                <option value="confirmed">Confirmado</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Status
              </label>
              <select
                className={SELECT_CLASS + ' w-full'}
                value={filters.isActive}
                onChange={(e) =>
                  updateFilter('isActive', e.target.value as '' | 'active' | 'inactive')
                }
              >
                <option value="">Todos</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Fornecedor
              </label>
              <select
                className={SELECT_CLASS + ' w-full'}
                value={filters.supplierId}
                onChange={(e) => updateFilter('supplierId', e.target.value)}
              >
                <option value="">Todos</option>
                {fornecedores.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Departamento
              </label>
              <select
                className={SELECT_CLASS + ' w-full'}
                value={filters.departmentId}
                onChange={(e) => updateFilter('departmentId', e.target.value)}
              >
                <option value="">Todos</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.display_name || d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                De
              </label>
              <Input
                type="date"
                className="w-full min-w-0"
                value={filters.dateFrom}
                onChange={(e) => updateFilter('dateFrom', e.target.value)}
              />
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Até
              </label>
              <Input
                type="date"
                className="w-full min-w-0"
                value={filters.dateTo}
                onChange={(e) => updateFilter('dateTo', e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/30">
                    <tr>
                      <SortableTableHeader
                        sortKey="name"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Nome
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="number"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Número
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="supplier"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Fornecedor
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="department"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Departamento
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="amount"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Valor vigente
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="termination"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Data de término
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="status"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Situação
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="active"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Status
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pageRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          Nenhum contrato encontrado.
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((row) => {
                        const openTotal = getOpenAmountsTotal(row.amounts)
                        const isDraft = row.status !== 'confirmed'
                        return (
                          <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/20">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {row.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {row.contract_number || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {row.supplier?.name || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {row.department?.display_name || row.department?.name || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {openTotal > 0 ? formatCurrency(openTotal) : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {formatDateOnlyPtBr(row.termination_date)}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${draftStatusBadgeClass(row.status)}`}
                              >
                                {contractStatusLabel(row.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${activeBadgeClass(row.is_active)}`}
                              >
                                {row.is_active ? 'Ativo' : 'Inativo'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => openView(row)}
                                  className={ICON_BTN_CLASS}
                                  title="Detalhes"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                                {isDraft && canUpdate && (
                                  <PermissionGuard
                                    moduleName={MODULE_NAME}
                                    permissionType="update"
                                    loadingComponent={PERM_LOADING}
                                    fallback={null}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openConfirm(row)}
                                      className={ICON_BTN_CLASS}
                                      title="Confirmar contrato"
                                    >
                                      <CheckIcon className="h-4 w-4" />
                                    </button>
                                  </PermissionGuard>
                                )}
                                {isDraft && canUpdate && (
                                  <PermissionGuard
                                    moduleName={MODULE_NAME}
                                    permissionType="update"
                                    loadingComponent={PERM_LOADING}
                                    fallback={null}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openEdit(row)}
                                      className={ICON_BTN_CLASS}
                                      title="Editar"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  </PermissionGuard>
                                )}
                                {isDraft && canDelete && (
                                  <PermissionGuard
                                    moduleName={MODULE_NAME}
                                    permissionType="delete"
                                    loadingComponent={PERM_LOADING}
                                    fallback={null}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => void handleDelete(row)}
                                      className={ICON_BTN_DANGER_CLASS}
                                      title="Excluir"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </PermissionGuard>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <EstoquePagination
            page={page}
            total={sorted.length}
            onPageChange={setPage}
            pageSize={PAGE_SIZE}
          />
        </div>
      </PermissionGuard>

      <ContratosFormModal
        open={modalOpen}
        editing={editing}
        busy={saving || confirmBusy || Boolean(confirming)}
        onClose={() => {
          if (saving || confirmBusy || confirming) return
          setModalOpen(false)
          setEditingContract(null)
        }}
        onSave={handleSave}
        onConclude={handleConcludeFromModal}
        onEditingChange={(contract) => {
          setEditingContract(contract)
          void load()
        }}
      />

      <ContratosConfirmModal
        open={Boolean(confirming)}
        contract={confirming}
        busy={confirmBusy}
        onClose={() => {
          if (!confirmBusy) closeConfirmModal(false)
        }}
        onConfirm={handleConfirmModalConfirm}
      />

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setViewing(null)} />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                {viewing.name}
              </Heading>
              <Button type="button" variant="outline" size="sm" onClick={() => setViewing(null)}>
                Fechar
              </Button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Text size="xs" color="muted">
                    Situação
                  </Text>
                  <Text>{contractStatusLabel(viewing.status)}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Status
                  </Text>
                  <Text>{viewing.is_active ? 'Ativo' : 'Inativo'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Número do contrato
                  </Text>
                  <Text>{viewing.contract_number || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Nome do contato
                  </Text>
                  <Text>{viewing.contact_name || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Telefone
                  </Text>
                  <Text>{viewing.contact_phone || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Email
                  </Text>
                  <Text>{viewing.contact_email || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Responsável
                  </Text>
                  <Text>{viewing.responsible?.name || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Fornecedor
                  </Text>
                  <Text>{viewing.supplier?.name || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Departamento
                  </Text>
                  <Text>
                    {viewing.department?.display_name || viewing.department?.name || '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Campanha
                  </Text>
                  <Text>{viewing.campaign?.name || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Evento
                  </Text>
                  <Text>{viewing.event?.name || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Solicitação
                  </Text>
                  <Text>
                    {viewing.request
                      ? formatRequestNumber(viewing.request.request_number)
                      : '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Total vigente
                  </Text>
                  <Text>
                    {viewingOpenTotal > 0 ? formatCurrency(viewingOpenTotal) : '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Adesão
                  </Text>
                  <Text>{formatDateOnlyPtBr(viewing.adhesion_date)}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Tipo de renovação
                  </Text>
                  <Text>
                    {viewing.renewal_type
                      ? viewing.renewal_type === 'recorrente'
                        ? `${RENEWAL_LABELS[viewing.renewal_type]} (pela adesão)`
                        : RENEWAL_LABELS[viewing.renewal_type]
                      : '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Data de término
                  </Text>
                  <Text>{formatDateOnlyPtBr(viewing.termination_date)}</Text>
                </div>
                {viewing.description && (
                  <div className="col-span-2">
                    <Text size="xs" color="muted">
                      Descrição
                    </Text>
                    <Text>{viewing.description}</Text>
                  </div>
                )}
              </div>

              <div>
                <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Serviços
                </Text>
                {(viewing.amounts || []).length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum serviço informado.
                  </Text>
                ) : (
                  <ul className="space-y-2">
                    {(viewing.amounts || []).map((amount) => (
                      <li
                        key={amount.id}
                        className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(Number(amount.amount))}
                          {isContractAmountCurrent(amount) ? (
                            <span className="ml-2 text-xs font-normal text-primary-600 dark:text-primary-300">
                              vigente
                            </span>
                          ) : (
                            <span className="ml-2 text-xs font-normal text-gray-400">
                              histórico
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {amount.notes || 'Sem descrição do serviço'}
                          {amount.department
                            ? ` · ${amount.department.display_name || amount.department.name}`
                            : ''}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateOnlyPtBr(amount.effective_from)}
                          {amount.effective_to
                            ? ` até ${formatDateOnlyPtBr(amount.effective_to)}`
                            : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Formas de pagamento
                </Text>
                {(viewing.payments || []).length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum pagamento informado.
                  </Text>
                ) : (
                  <ul className="space-y-2">
                    {(viewing.payments || []).map((payment) => (
                      <li
                        key={payment.id}
                        className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {payment.payment_method?.name || 'Forma'} · parcela{' '}
                          {payment.installment_number}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatCurrency(payment.amount)} · venc.{' '}
                          {formatDateOnlyPtBr(payment.due_date)}
                          {payment.payment_status === 'paid' ? ' · pago' : ' · pendente'}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Anexos
                </Text>
                {(viewing.attachments || []).length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum anexo.
                  </Text>
                ) : (
                  <ul className="space-y-2">
                    {(viewing.attachments || []).map((att) => (
                      <li
                        key={att.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                      >
                        <span className="truncate text-gray-700 dark:text-gray-200">
                          {att.file_name}
                        </span>
                        <button
                          type="button"
                          onClick={() => void handleOpenAttachment(att.file_path)}
                          className={ICON_BTN_CLASS}
                          title="Abrir"
                        >
                          <DocumentArrowDownIcon className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketingContratos
